/**
 * SystemBuilderNav - Navigation component for subsystem and layer management
 *
 * Handles:
 * - Subsystem toggling between different system configurations
 * - Layer navigation within subsystems
 * - Tab switching for desktop and mobile views
 * - Active state management for layers and subsystems
 * - Event emission for state changes
 *
 * Emits:
 * - SUBSYSTEM_CHANGED: When user switches between subsystems
 * - LAYER_CHANGED: When user navigates to a different layer
 *
 * @class
 * @extends {window.BAO.CustomElement}
 */

const { getOffsetTop } = window.BAO.Utils

class SystemBuilderNav extends window.BAO.CustomElement() {
  connectedCallback () {
    window.BAO.SystemBuilder.attach(this)
    super.connectedCallback()
  }

  setupListeners () {
    super.setupListeners()

    this.sharedEls?.['subsystem-toggle']?.elements.forEach((toggleEl) => {
      this.listeners.add(
        toggleEl,
        'click',
        this.handleSubsystemToggle.bind(this),
      )
    })

    this.sharedEls?.['layer-trigger']?.elements.forEach((triggerEl) => {
      this.listeners.add(triggerEl, 'click', this.handleLayerClick.bind(this))
    })

    this.sharedEls?.tab?.elements.forEach((tabEl) => {
      this.listeners.add(tabEl, 'click', this.handleTabClick.bind(this))
    })

    this.sharedEls?.['layer-product']?.elements.forEach((productEl) => {
      this.listeners.add(
        productEl,
        'click',
        this.handleProductsDrawerRedirect.bind(this),
      )
    })

    this.sharedEls?.['edit-layer']?.elements.forEach((editEl) => {
      this.listeners.add(
        editEl,
        'click',
        this.handleProductsDrawerRedirect.bind(this),
      )
    })

    this.sharedEls?.hotspot?.elements.forEach((hotspotEl) => {
      this.listeners.add(
        hotspotEl,
        'click',
        this.handleProductsDrawerRedirect.bind(this),
      )
    })

    if (
      window.BAO.eventBus &&
      window.BAO.EVENTS?.SYSTEM_BUILDER?.SUBSYSTEM_CHANGED
    ) {
      window.BAO.eventBus.addEventListener(
        window.BAO.EVENTS.SYSTEM_BUILDER.SUBSYSTEM_CHANGED,
        this.#onSubsystemChangedFromExternal.bind(this),
      )
    }

    this.desktopQuery = window.matchMedia('(min-width: 894px)')
    this.handleDesktopInert(this.desktopQuery)
    this.desktopQuery.addEventListener(
      'change',
      this.handleDesktopInert.bind(this),
    )
  }

  /**
   * Toggle inert on layer-product and hotspot elements based on viewport
   *
   * On desktop, these elements should be inert since users interact
   * via the products panel. On mobile they remain interactive.
   *
   * @param {MediaQueryList|MediaQueryListEvent} mq - Media query match
   */
  handleDesktopInert (mq) {
    const isDesktop = mq.matches

    this.sharedEls?.['layer-product']?.elements.forEach((el) => {
      const layerEl = el.closest(this.sharedEls?.layer?.selector)
      const isOpen = layerEl?.getAttribute('aria-expanded') === 'true'
      el.toggleAttribute('inert', isDesktop && !isOpen)
    })
  }

  /**
   * Handle clicks on layer products, edit buttons, and hotspots
   *
   * Opens the products drawer on mobile when clicking product placeholders
   * Sets the active layer to the clicked layer
   *
   * @param {Event} event - Click event
   */
  #onSubsystemChangedFromExternal (event) {
    const subSystemIndex = event.detail?.subSystemIndex
    if (subSystemIndex === undefined || isNaN(subSystemIndex)) return

    this.state.subSystemIndex = subSystemIndex
    this.switchToSubSystem(subSystemIndex)
  }

  handleProductsDrawerRedirect (event) {
    const hotspot = event.target.closest(this.sharedEls?.hotspot?.selector)

    if (hotspot?.getAttribute('aria-hidden') === 'true') return

    const target = hotspot || event.target
    const subSystemIndex = parseInt(target.dataset.systemSubsystemIndex)
    const layerIndex = parseInt(target.dataset.systemLayerIndex)
    const groupIndex = parseInt(
      target.dataset.systemGroupIndex ??
        target.closest('[data-system-group-index]')?.dataset.systemGroupIndex,
    )
    if (isNaN(subSystemIndex) || isNaN(layerIndex)) return

    this.setActiveLayer(subSystemIndex, layerIndex)
    this.openProductsAndScroll(subSystemIndex, layerIndex, groupIndex)
  }

  openProductsAndScroll (subSystemIndex, layerIndex, groupIndex) {
    const isMobile = window.innerWidth < 894

    if (isMobile) {
      window.drawers.open('system-builder-products')
    }

    if (isNaN(groupIndex)) return

    if (isMobile) {
      requestAnimationFrame(() => {
        this.scrollToGroupTitle(subSystemIndex, layerIndex, groupIndex)
      })
    } else {
      this.scrollToGroupTitle(subSystemIndex, layerIndex, groupIndex)
    }
  }

  scrollToGroupTitle (subSystemIndex, layerIndex, groupIndex) {
    const groupProducts = this.sharedEls?.['group-products']?.elements.find(
      (el) =>
        el.dataset.systemSubsystemIndex === String(subSystemIndex) &&
        el.dataset.systemLayerIndex === String(layerIndex) &&
        el.dataset.systemGroupIndex === String(groupIndex) &&
        el.offsetParent !== null,
    )

    if (!groupProducts) return

    this.sharedEls?.['group-products'].elements.forEach((groupProductEl) => {
      groupProductEl.classList.toggle(
        'sys-SystemBuilderLayer_ProductGrid-animate',
        false,
      )
    })

    const parentScrollContainer = groupProducts.closest(
      '[data-system-builder-el="scroll-column"]',
    )
    const drawerScrollContainer = groupProducts.closest('.drw-Drawer_Inner')
    const groupTitleHeight = 54
    const drawerTitle = 63

    if (parentScrollContainer) {
      const groupProductsOffsetTop = getOffsetTop(
        groupProducts,
        parentScrollContainer,
      )

      parentScrollContainer.scrollTo({
        top: groupProductsOffsetTop - groupTitleHeight,
        behavior: 'smooth',
      })
    }
    if (drawerScrollContainer) {
      const drawerProductsOffsetTop = getOffsetTop(
        groupProducts,
        drawerScrollContainer,
      )

      drawerScrollContainer.scrollTo({
        top: drawerProductsOffsetTop - groupTitleHeight - drawerTitle,
        behavior: 'instant',
      })
    }

    this.sharedEls?.['group-products'].elements.forEach((groupProductEl) => {
      groupProductEl.classList.toggle(
        'sys-SystemBuilderLayer_ProductGrid-animate',
        groupProductEl === groupProducts,
      )
    })
  }

  /**
   * Handle subsystem toggle button clicks
   *
   * Updates the active subsystem and switches all related UI elements
   * Emits SUBSYSTEM_CHANGED event for other components to react
   *
   * @param {Event} event - Click event from subsystem toggle button
   */
  handleSubsystemToggle (event) {
    const toggleEl = event.target.closest(
      this.sharedEls?.['subsystem-toggle']?.selector,
    )
    if (!toggleEl) return

    const newSubSystemIndex = parseInt(toggleEl.dataset.systemSubsystemIndex)
    if (isNaN(newSubSystemIndex)) return

    this.state.subSystemIndex = newSubSystemIndex
    this.switchToSubSystem(newSubSystemIndex)

    if (
      window.BAO.eventBus &&
      window.BAO.EVENTS?.SYSTEM_BUILDER?.SUBSYSTEM_CHANGED
    ) {
      window.BAO.eventBus.dispatchEvent(
        window.BAO.EVENTS.SYSTEM_BUILDER.SUBSYSTEM_CHANGED,
        { subSystemIndex: newSubSystemIndex },
      )
    }
  }

  /**
   * Switch to a specific subsystem
   *
   * Updates aria attributes for all subsystem-related elements
   * Automatically sets the first layer as active
   *
   * @param {number} subSystemIndex - Index of the subsystem to switch to
   */
  switchToSubSystem (subSystemIndex) {
    this.sharedEls['subsystem-toggle']?.elements.forEach((el) => {
      const elIndex = parseInt(el.dataset.systemSubsystemIndex)
      el.setAttribute(
        'aria-pressed',
        elIndex === subSystemIndex ? 'true' : 'false',
      )
    })

    this.sharedEls.subsystem?.elements.forEach((el) => {
      const elIndex = parseInt(el.dataset.systemSubsystemIndex)
      el.setAttribute(
        'aria-hidden',
        elIndex !== subSystemIndex ? 'true' : 'false',
      )
    })

    this.sharedEls['subsystem-content']?.elements.forEach((el) => {
      const elIndex = parseInt(el.dataset.systemSubsystemIndex)
      el.setAttribute(
        'aria-hidden',
        elIndex !== subSystemIndex ? 'true' : 'false',
      )
    })

    this.sharedEls['subsystem-tabs']?.elements.forEach((el) => {
      const elIndex = parseInt(el.dataset.systemSubsystemIndex)
      el.setAttribute(
        'aria-hidden',
        elIndex !== subSystemIndex ? 'true' : 'false',
      )
    })

    this.sharedEls['subsystem-mobile-tabs']?.elements.forEach((el) => {
      const elIndex = parseInt(el.dataset.systemSubsystemIndex)
      el.setAttribute(
        'aria-hidden',
        elIndex !== subSystemIndex ? 'true' : 'false',
      )
    })

    this.sharedEls['subsystem-products']?.elements.forEach((el) => {
      const elIndex = parseInt(el.dataset.systemSubsystemIndex)
      el.setAttribute(
        'aria-hidden',
        elIndex !== subSystemIndex ? 'true' : 'false',
      )
    })

    this.sharedEls['subsystem-drawer']?.elements.forEach((el) => {
      const elIndex = parseInt(el.dataset.systemSubsystemIndex)
      el.setAttribute(
        'aria-hidden',
        elIndex !== subSystemIndex ? 'true' : 'false',
      )
    })

    this.sharedEls['subsystem-drawer-tabs']?.elements.forEach((el) => {
      const elIndex = parseInt(el.dataset.systemSubsystemIndex)
      el.setAttribute(
        'aria-hidden',
        elIndex !== subSystemIndex ? 'true' : 'false',
      )
    })

    this.setActiveLayer(subSystemIndex, 0)
  }

  /**
   * Handle layer trigger clicks (accordion-style navigation)
   *
   * Toggles layer expansion in the sidebar navigation
   *
   * @param {Event} event - Click event from layer trigger button
   */
  handleLayerClick (event) {
    const triggerEl = event.target.closest(
      this.sharedEls?.['layer-trigger']?.selector,
    )
    if (!triggerEl) return

    const layerEl = triggerEl.closest(this.sharedEls?.layer?.selector)
    if (!layerEl) return

    const subSystemEl = layerEl.closest('[data-system-subsystem-index]')
    const subSystemIndex = parseInt(subSystemEl?.dataset.systemSubsystemIndex)
    if (isNaN(subSystemIndex)) return

    const layerContainer = layerEl.closest('.sys-SystemBuilder_Layers')
    const layerIndex = layerContainer
      ? Array.from(layerContainer.children).indexOf(layerEl)
      : -1

    if (layerIndex === -1) return

    this.setActiveLayer(subSystemIndex, layerIndex)
  }

  /**
   * Handle tab clicks (desktop and mobile tab navigation)
   *
   * Switches between layers using tab interface
   *
   * @param {Event} event - Click event from tab element
   */
  handleTabClick (event) {
    const clickedTabEl = event.target.closest(this.sharedEls?.tab?.selector)
    if (!clickedTabEl) return

    const subSystemEl = clickedTabEl.closest('[data-system-subsystem-index]')
    const subSystemIndex = parseInt(subSystemEl?.dataset.systemSubsystemIndex)
    if (isNaN(subSystemIndex)) return

    const tabItem = clickedTabEl.closest(
      '.sys-SystemBuilder_TabItem, .sys-SystemBuilder_MobileTabItem',
    )
    const tabsList = tabItem?.parentElement
    const layerIndex = tabsList
      ? Array.from(tabsList.children).indexOf(tabItem)
      : -1

    if (layerIndex === -1) return

    this.setActiveLayer(subSystemIndex, layerIndex)
  }

  /**
   * Set the active layer for a subsystem
   *
   * Updates all UI elements to reflect the active layer:
   * - Tab selection states
   * - Layer expansion states
   * - Image visibility
   * - Hotspot visibility
   * - Product container visibility
   * - Drawer content visibility
   *
   * Updates state and emits LAYER_CHANGED event
   *
   * @param {number} subSystemIndex - Index of the subsystem
   * @param {number} layerIndex - Index of the layer within the subsystem
   */
  setActiveLayer (subSystemIndex, layerIndex) {
    this.sharedEls.tab?.elements.forEach((tabEl) => {
      const tabSubsystem = parseInt(
        tabEl.closest('[data-system-subsystem-index]')?.dataset
          .systemSubsystemIndex,
      )
      const tabContainer = tabEl.closest(
        '.sys-SystemBuilder_TabItem, .sys-SystemBuilder_MobileTabItem',
      )
      const tabIndex = tabContainer
        ? Array.from(tabContainer.parentElement.children).indexOf(tabContainer)
        : -1

      if (tabSubsystem === subSystemIndex) {
        tabEl.setAttribute(
          'aria-current',
          tabIndex === layerIndex ? 'true' : 'false',
        )
      }
    })

    this.sharedEls.layer?.elements.forEach((layerEl) => {
      const layerSubsystem = parseInt(
        layerEl.closest('[data-system-subsystem-index]')?.dataset
          .systemSubsystemIndex,
      )
      const layerContainer = layerEl.closest('.sys-SystemBuilder_Layers')
      const layerIdx = layerContainer
        ? Array.from(layerContainer.children).indexOf(layerEl)
        : -1

      if (layerSubsystem === subSystemIndex) {
        const isActive = layerIdx === layerIndex
        layerEl.setAttribute('aria-expanded', isActive ? 'true' : 'false')

        const groupsEl = layerEl.querySelector('.sys-SystemBuilderGroups')
        if (groupsEl) {
          groupsEl.toggleAttribute('inert', !isActive)
        }

        const editEl = layerEl.querySelector(
          this.sharedEls?.['edit-layer']?.selector,
        )
        if (editEl) {
          editEl.toggleAttribute('inert', !isActive)
        }
      }
    })

    this.sharedEls['subsystem-content']?.elements.forEach((subsystemEl) => {
      const subsystemIdx = parseInt(subsystemEl.dataset.systemSubsystemIndex)
      if (subsystemIdx === subSystemIndex) {
        const images = subsystemEl.querySelectorAll(
          this.sharedEls.image.selector,
        )
        images.forEach((imageEl, i) => {
          imageEl.setAttribute(
            'aria-hidden',
            i !== layerIndex ? 'true' : 'false',
          )
        })

        const hotspots = subsystemEl.querySelectorAll(
          this.sharedEls.hotspots.selector,
        )
        hotspots.forEach((hotspotsEl, i) => {
          hotspotsEl.setAttribute(
            'aria-hidden',
            i !== layerIndex ? 'true' : 'false',
          )
        })
      }
    })

    this.sharedEls['subsystem-products']?.elements.forEach((subsystemEl) => {
      const subsystemIdx = parseInt(subsystemEl.dataset.systemSubsystemIndex)
      if (subsystemIdx === subSystemIndex) {
        const products = subsystemEl.querySelectorAll(
          this.sharedEls.products.selector,
        )

        this.sharedEls?.['group-products'].elements.forEach(
          (groupProductEl) => {
            groupProductEl.classList.toggle(
              'sys-SystemBuilderLayer_ProductGrid-animate',
              false,
            )
          },
        )

        products.forEach((productsEl, i) => {
          const currentState = productsEl.getAttribute('aria-hidden')

          productsEl.setAttribute(
            'aria-hidden',
            i !== layerIndex ? 'true' : 'false',
          )

          if (i === layerIndex && currentState === 'true') {
            const parentScrollContainer = productsEl.closest(
              '[data-system-builder-el="scroll-column"]',
            )

            parentScrollContainer.scrollTo({ top: 0, behavior: 'instant' })
          }
        })
      }
    })

    if (this.sharedEls['drawer-content']?.elements) {
      this.sharedEls['drawer-content'].elements.forEach((drawerContentEl) => {
        const drawerSubsystem = parseInt(
          drawerContentEl.closest('[data-system-subsystem-index]')?.dataset
            .systemSubsystemIndex,
        )
        const drawerContainer = drawerContentEl.parentElement
        const drawerIdx = drawerContainer
          ? Array.from(
            drawerContainer.querySelectorAll(
              this.sharedEls['drawer-content'].selector,
            ),
          ).indexOf(drawerContentEl)
          : -1

        if (drawerSubsystem === subSystemIndex) {
          const currentState = drawerContentEl.getAttribute('aria-hidden')

          drawerContentEl.setAttribute(
            'aria-hidden',
            drawerIdx !== layerIndex ? 'true' : 'false',
          )

          if (drawerIdx === layerIndex && currentState === 'true') {
            const drawerEl = drawerContentEl.closest('.drw-Drawer_Inner')

            drawerEl.scrollTo({ top: 0, behavior: 'instant' })
          }
        }
      })
    }

    if (window.innerWidth >= 894) {
      const systemBuilder = this.closest('system-builder')
      if (systemBuilder) {
        systemBuilder.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    }

    this.state.subSystemIndex = subSystemIndex
    this.state.layerIndex = layerIndex
    this.state.flatLayerIndex = this.state.getFlatLayerIndex(
      subSystemIndex,
      layerIndex,
    )

    this.handleDesktopInert(this.desktopQuery)

    if (
      window.BAO.eventBus &&
      window.BAO.EVENTS?.SYSTEM_BUILDER?.LAYER_CHANGED
    ) {
      window.BAO.eventBus.dispatchEvent(
        window.BAO.EVENTS.SYSTEM_BUILDER.LAYER_CHANGED,
        { subSystemIndex, layerIndex },
      )
    }
  }
}

if (!customElements.get('system-builder-nav')) {
  customElements.define('system-builder-nav', SystemBuilderNav)
}
